/*
PURPOSE: Individual coral card component displaying coral details and artist grid
USE CASES:
- Display coral header with name, contribution amount, and join date
- Show grid of artists supported in this coral
- Provide visual separation between coral information and artist content
DOMAIN BOUNDARIES:
- Responsible for rendering a single coral and its artists
- Delegates artist rendering to ArtistCard
- Does not modify coral data
ASSUMPTIONS:
- Coral data includes artist pool with artist list
- Artist information should be displayed in a grid layout
- Each coral is visually distinct with proper spacing
*/

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilteredClientCoral, ClientCoral, CoralSubscriptionStatus } from 'shared/types/platformTypes';
import { GetCoralSubscriptionStatusDescription } from '../../utils/transformData';
import { processSingleArtistSelection, extractActiveFilters } from 'shared/utils/coralProcessingUtils';
import CoralChart from '../CoralChart/CoralChart';
import ArtistCard from './ArtistCard';
import CoralButton from '../CoralButtons/CoralButton';
import AllInOneCoralCreator from '../AllInOneCoralCreator/AllInOneCoralCreator';

// Add custom CSS animations for modern transitions
const styles = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .coral-card-expanding {
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  
  .coral-card-inactive .artists-section {
    transition: all 0.3s ease-in-out;
  }
  
  @media (prefers-reduced-motion: reduce) {
    .coral-card-expanding,
    .coral-card-inactive .artists-section,
    .creator-fadein {
      transition: opacity 0.2s ease;
    }
  }
`;

// Inject styles into document head
if (typeof document !== 'undefined' && !document.getElementById('coral-card-animations')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'coral-card-animations';
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}

// Helper function to get badge color class based on subscription status
const getBadgeColorClass = (status: CoralSubscriptionStatus): string => {
    switch (status) {
        case CoralSubscriptionStatus.ACTIVE:
            return 'bg-gradient-to-r from-green-400 to-green-500 text-white shadow-green-500/25';
        case CoralSubscriptionStatus.PENDING:
            return 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white shadow-yellow-500/25';
        case CoralSubscriptionStatus.PAUSED:
            return 'bg-gradient-to-r from-blue-400 to-blue-500 text-white shadow-blue-500/25';
        case CoralSubscriptionStatus.NONE:
        default:
            return 'bg-gradient-to-r from-gray-400 to-gray-500 text-white shadow-gray-500/25';
    }
};

// Type guard to check if coral is ClientCoral
const isClientCoral = (coral: FilteredClientCoral | ClientCoral): coral is ClientCoral => {
  // Primary check: has subscriptionStatus (proper ClientCoral)
  if ('subscriptionStatus' in coral) return true;
  // Fallback: if has userId, likely user's own coral (missing field due to old data)
  return 'userId' in coral;
};

interface CoralCardProps {
  coral: FilteredClientCoral | ClientCoral;
  variant?: 'dashboard' | 'profile';
  showSubscriptionFeatures?: boolean;
  enableCoralNavigation?: boolean;
}

const CoralCard: React.FC<CoralCardProps> = ({ 
  coral, 
  variant = 'profile',
  showSubscriptionFeatures = false,
  enableCoralNavigation = false
}) => {
  const navigate = useNavigate();
  const [showAllArtists, setShowAllArtists] = useState(false);
  const [animationState, setAnimationState] = useState<'inactive' | 'expanding' | 'active'>('inactive');
  const isClientCoralType = isClientCoral(coral);
  // Treat missing subscriptionStatus as inactive (backwards compatible with old coral data)
  const isInactiveCore = isClientCoralType && 
    (coral.subscriptionStatus === undefined || coral.subscriptionStatus !== CoralSubscriptionStatus.ACTIVE);
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  // Calculate filtered artists and genre data for dashboard variant
  const activeFilters = isClientCoralType ? extractActiveFilters(coral) : null;
  const filteredArtists = isClientCoralType && activeFilters
    ? coral.artistPool.artistList.filter(artist => 
        processSingleArtistSelection(artist, activeFilters)
      )
    : coral.artistPool?.artistList || [];

  const genreData = variant === 'dashboard' && filteredArtists.length > 0 
    ? filteredArtists.reduce((acc, artist) => {
        const genre = artist.artistData?.primaryGenre || 'Unknown';
        const amount = coral.pledgeAmount / filteredArtists.length;
        acc[genre] = (acc[genre] || 0) + amount;
        return acc;
      }, {} as Record<string, number>)
    : {};
    
  const aggregatedGenreData = Object.entries(genreData).map(([name, amount]) => ({
    name,
    amount
  }));

  const handleCardClick = () => {
    if (enableCoralNavigation && !isInactiveCore) {
      navigate(`/coral/${coral.guid}`);
    }
  };

  const handlePledgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnimationState('expanding');
    setTimeout(() => setAnimationState('active'), 400);
  };

  const handleBackClick = () => {
    setAnimationState('inactive');
  };

  // Show first 6 artists initially for dashboard, or all artists for profile
  const displayArtists = variant === 'dashboard' 
    ? (showAllArtists ? filteredArtists : filteredArtists.slice(0, 6))
    : coral.artistPool?.artistList || [];
  const hasMoreArtists = variant === 'dashboard' && filteredArtists.length > 6;

  if (variant === 'dashboard') {
    // Modern animation CSS classes
    const cardClasses = `
      bg-white/95 backdrop-blur-[0.5px] rounded-xl shadow-sm border border-gray-100/20 overflow-hidden
      transition-all duration-500 ease-out
      ${animationState === 'expanding' ? 'coral-card-expanding' : ''}
      ${animationState === 'active' ? 'coral-card-active' : ''}
      ${enableCoralNavigation && !isInactiveCore ? 'cursor-pointer hover:shadow-md' : ''}
      ${isInactiveCore ? 'coral-card-inactive' : ''}
    `.trim();

    return (
      <div 
        className={cardClasses}
        onClick={handleCardClick}
        data-testid={`coral-card-${coral.guid}`}
        style={{
          ...(animationState === 'expanding' && {
            transform: 'scale(1.02)',
            filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.1))'
          })
        }}
      >
        {/* INACTIVE CORAL STATE */}
        {isInactiveCore && animationState === 'inactive' && (
          <>
            {/* Minimal Header with Just Pledge Button */}
            <div className="px-6 py-5 border-b border-gray-100/50">
              <div className="flex justify-center">
                <CoralButton 
                  onClick={handlePledgeClick}
                  popOnHover={true}
                  className="pledge-button transition-transform duration-100 active:scale-95"
                >
                  Pledge
                </CoralButton>
              </div>
            </div>
            
            {/* Artists Section */}
            <div className="p-6 transition-all duration-300 ease-in-out artists-section">
              {displayArtists.length > 0 ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-medium text-gray-700">
                      Backed Artists
                    </h4>
                    <div className="h-px flex-1 bg-gray-200 ml-4"></div>
                    {hasMoreArtists && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAllArtists(!showAllArtists);
                        }}
                        className="ml-4 text-xs font-medium text-coral-pink hover:text-coral-orange transition-colors"
                      >
                        {showAllArtists ? 'Show less' : `Show ${filteredArtists.length - displayArtists.length} more`}
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
                    {displayArtists.map((artist, index) => (
                      <ArtistCard
                        key={`${coral.guid}-${artist.artistName || artist.artistId || index}`}
                        artist={artist}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">No artists in this coral</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* EXPANDING STATE - Hide content during transition */}
        {animationState === 'expanding' && (
          <div className="p-6 flex items-center justify-center h-48">
            <div className="w-8 h-8 border-4 border-coral-pink border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {/* ACTIVE STATE - Show AllInOneCoralCreator */}
        {isInactiveCore && animationState === 'active' && (
          <div 
            className="creator-fadein"
            style={{
              animation: 'fadeInUp 0.3s ease-out 0.1s forwards',
              opacity: 0
            }}
          >
            <AllInOneCoralCreator 
              existingCoralId={coral.guid}
              transparent={false}
              lightMode={true}
              compact={true}
              onBack={handleBackClick}
            />
          </div>
        )}

        {/* ACTIVE CORAL STATE - Original UI */}
        {!isInactiveCore && (
          <>
            {/* Enhanced Dashboard Header */}
            <div className="px-6 py-5 border-b border-gray-100/50">
              <div className="flex justify-between gap-6 h-full">
                {/* Left side - Main coral info */}
                <div className="flex-grow flex flex-col justify-between">
                  {/* Top section - Amount and Contributing date grouped */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl font-bold text-gray-900">
                        ${coral.pledgeAmount || 0}
                      </span>
                      <span className="text-gray-500">per month</span>
                    </div>
                    <div className="text-gray-600 text-sm">
                      Since {formatDate(coral.createdAt)}
                    </div>
                    <div className="mt-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/coral/${coral.guid}`);
                        }}
                        className="text-coral-orange hover:text-coral-pink text-sm font-medium underline transition-colors"
                      >
                        Manage
                      </button>
                    </div>
                  </div>
                  
                  {/* Bottom section - Status */}
                  <div>
                    {showSubscriptionFeatures && isClientCoralType && (
                      <span className={`inline-block text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm ${getBadgeColorClass(coral.subscriptionStatus)}`}>
                        {GetCoralSubscriptionStatusDescription(coral.subscriptionStatus)}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Right side - Genre Distribution */}
                {aggregatedGenreData.length > 0 && (
                  <div className="flex-shrink-0">
                    <div className="text-center">
                      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                        Genre Distribution
                      </p>
                      <CoralChart
                        width={120}
                        height={120}
                        data={aggregatedGenreData}
                        showAmounts={false}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Artist Grid - Full Width Netflix Style */}
            <div className="p-6">
              {displayArtists.length > 0 ? (
                <>
                  {/* Artist Grid Header */}
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-medium text-gray-700">
                      Backed Artists
                    </h4>
                    <div className="h-px flex-1 bg-gray-200 ml-4"></div>
                    {/* Show more/less button in header */}
                    {hasMoreArtists && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAllArtists(!showAllArtists);
                        }}
                        className="ml-4 text-xs font-medium text-coral-pink hover:text-coral-orange transition-colors"
                        data-testid="coral-show-more-artists"
                      >
                        {showAllArtists ? 'Show less' : `Show ${filteredArtists.length - displayArtists.length} more`}
                      </button>
                    )}
                  </div>
                  
                  {/* Artist Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
                    {displayArtists.map((artist, index) => (
                      <ArtistCard
                        key={`${coral.guid}-${artist.artistName || artist.artistId || index}`}
                        artist={artist}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm">No artists in this coral</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  // Profile variant (original layout)
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-300">
      {/* Coral Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <span className="text-lg font-semibold">
            ${coral.pledgeAmount || 0}
          </span>
          <span className="text-sm text-gray-600">
            Contributing since {formatDate(coral.createdAt)}
          </span>
        </div>
      </div>

      {/* Artist Grid */}
      <div className="p-6">
        {coral.artistPool?.artistList && coral.artistPool.artistList.length > 0 ? (
          <>
            {/* Artist Grid Header */}
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-medium text-gray-700">
                Backed Artists
              </h4>
              <div className="h-px flex-1 bg-gray-200 ml-4"></div>
            </div>
            
            {/* Artist Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
              {coral.artistPool.artistList.map((artist, index) => (
                <ArtistCard
                  key={`${coral.guid}-${artist.artistName || artist.artistId || index}`}
                  artist={artist}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p className="text-sm">No artists in this coral</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoralCard;